import type { AxiosRequestConfig } from 'axios';
import { api } from '@/lib/api';
import type {
  ChecklistLinkedEntityType,
  ChecklistQuery,
  ChecklistResponse,
  CreateChecklistRequest,
  ListResponse,
  ReviewChecklistRequest,
  SubmitChecklistRequest,
  UpdateChecklistRequest,
} from '@troia-v3/types';

/**
 * Suprime o `toast.error` genérico do interceptor global (`src/lib/api.ts`)
 * em `byEntity` — a aba Checklists do drawer de lead/ticket (Task 17) trata
 * um 404 (user sem acesso ao pai — não deveria acontecer dentro do próprio
 * drawer, mas o backend valida mesmo assim) como estado vazio silencioso, sem
 * toast vermelho. Mesmo padrão de `customersService`/`ticketsService`.
 */
const HANDLE_ERROR_LOCALLY: AxiosRequestConfig & { _handleErrorLocally: boolean } = {
  _handleErrorLocally: true,
};

/**
 * checklistsApi — client HTTP do módulo `checklists` (Tasks 6/9 do backend).
 * Molde estrutural: `automation/forms/services/formsApi.ts`.
 *
 * Consumido por esta task (`ChecklistsPage`/`ChecklistReviewPage`) e pelas
 * Tasks 16-17 (Meus checklists / preenchimento no campo, aba do
 * lead/ticket).
 */
export const checklistsApi = {
  /** GET /api/checklists — listagem de gestão (paginada, `checklists:read`). */
  async list(query?: ChecklistQuery): Promise<ListResponse<ChecklistResponse>> {
    const response = await api.get('/checklists', { params: query });
    return response.data.data;
  },

  /** GET /api/checklists/:id — detalhe (assignee OU follower OU `checklists:read`). */
  async getById(id: string): Promise<ChecklistResponse> {
    const response = await api.get(`/checklists/${id}`);
    return response.data.data;
  },

  /** POST /api/checklists — cria uma instância a partir de um modelo (`checklists:create`). */
  async create(data: CreateChecklistRequest): Promise<ChecklistResponse> {
    const response = await api.post('/checklists', data);
    return response.data.data;
  },

  /** PUT /api/checklists/:id — gestão (responsável/seguidores/prazo/unidade/vínculo). */
  async update(id: string, data: UpdateChecklistRequest): Promise<ChecklistResponse> {
    const response = await api.put(`/checklists/${id}`, data);
    return response.data.data;
  },

  /**
   * POST /api/checklists/:id/review — `completed` → `approved`/`rejected`.
   * `comment` é OBRIGATÓRIO quando `decision === 'rejected'` (validado no
   * backend via union discriminada; a UI também valida antes de enviar).
   */
  async review(id: string, data: ReviewChecklistRequest): Promise<ChecklistResponse> {
    const response = await api.post(`/checklists/${id}/review`, data);
    return response.data.data;
  },

  /** POST /api/checklists/:id/reopen — `rejected`/`expired` → `in_progress`, prazo novo. */
  async reopen(id: string): Promise<ChecklistResponse> {
    const response = await api.post(`/checklists/${id}/reopen`);
    return response.data.data;
  },

  /** GET /api/checklists/my — caixa de entrada do operador (assignee OU follower). */
  async myChecklists(query?: ChecklistQuery): Promise<ListResponse<ChecklistResponse>> {
    const response = await api.get('/checklists/my', { params: query });
    return response.data.data;
  },

  /** GET /api/checklists/by-entity?type=&id= — aba "Checklists" dos drawers de lead/ticket. */
  async byEntity(type: ChecklistLinkedEntityType, id: string): Promise<ChecklistResponse[]> {
    const response = await api.get('/checklists/by-entity', { params: { type, id }, ...HANDLE_ERROR_LOCALLY });
    return response.data.data;
  },

  /**
   * POST /api/checklists/:id/start — `pending` → `in_progress` (assignee-only).
   *
   * `options._handleErrorLocally` (convenção do interceptor em `@/lib/api`)
   * silencia o toast global de erro. A `ChecklistFillPage` usa isso porque o
   * 409 ("já in_progress") é benigno ali — duas abas na mesma instância, ou um
   * refetch entre o `getById` e o `start` — e não deve virar erro na cara do
   * gerente em campo.
   */
  async start(
    id: string,
    // `signal` faz parte da assinatura por simetria com os helpers de `@/lib/api`
    // — e porque um objeto SÓ com `_handleErrorLocally` não teria propriedade em
    // comum com `AxiosRequestConfig` (weak type detection do TS).
    options?: { signal?: AbortSignal; _handleErrorLocally?: boolean },
  ): Promise<ChecklistResponse> {
    const response = await api.post(`/checklists/${id}/start`, undefined, options);
    return response.data.data;
  },

  /** POST /api/checklists/:id/submit — `in_progress` → `completed`/`approved` (assignee-only). */
  async submit(id: string, data: SubmitChecklistRequest): Promise<ChecklistResponse> {
    const response = await api.post(`/checklists/${id}/submit`, data);
    return response.data.data;
  },
};
